// ============================================================
//  SiteContext — Cloud-aware data provider
//  Reads from Supabase (production) or backend (local dev)
// ============================================================

import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  getSettings, getWeddings, getFilms,
  getPrewedding, getInstagram, getTestimonials,
  getPreweddingSessions,
} from "@/lib/cloudApi";
import { fileUrl } from "@/lib/api";

const SiteContext = createContext(null);

function preloadImage(src) {
  const resolvedSrc = fileUrl(src);
  if (!resolvedSrc || typeof Image === "undefined") return Promise.resolve();

  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    image.onload = finish;
    image.onerror = finish;
    image.src = resolvedSrc;

    if (image.complete) finish();
    window.setTimeout(finish, 8000);
  });
}

export const SiteProvider = ({ children }) => {
  const initialLoadDone = useRef(false);
  const [settings,     setSettings]     = useState(null);
  const [weddings,     setWeddings]     = useState([]);
  const [films,        setFilms]        = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [prewedding,   setPrewedding]   = useState([]);
  const [preweddingSessions, setPreweddingSessions] = useState([]);
  const [instagram,    setInstagram]    = useState([]);
  const [siteLoading,  setSiteLoading]  = useState(true);
  const [isSiteReady,  setIsSiteReady]  = useState(false);

  const refresh = useCallback(async () => {
    const firstLoad = !initialLoadDone.current;
    if (firstLoad) setSiteLoading(true);

    try {
      const [s, w, f, t, p, i, pws] = await Promise.all([
        getSettings(),
        getWeddings(),
        getFilms(),
        getTestimonials(),
        getPrewedding(),
        getInstagram(),
        getPreweddingSessions(),
      ]);

      if (firstLoad) {
        await Promise.all([
          preloadImage(s?.logoUrl),
          preloadImage(s?.heroImage),
        ]);
      }

      if (s) setSettings(s);
      if (w) setWeddings(Array.isArray(w) ? w : Object.values(w));
      if (f) setFilms(Array.isArray(f) ? f : Object.values(f));
      if (t) setTestimonials(Array.isArray(t) ? t : Object.values(t));
      if (p) setPrewedding(Array.isArray(p) ? p : Object.values(p));
      if (pws) setPreweddingSessions(Array.isArray(pws) ? pws : Object.values(pws));
      if (i) setInstagram(Array.isArray(i) ? i : Object.values(i));
    } catch (e) {
      console.error("Site data load failed", e);
    } finally {
      initialLoadDone.current = true;
      setIsSiteReady(true);
      setSiteLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SiteContext.Provider value={{
      settings, weddings, films, testimonials,
      prewedding, preweddingSessions, instagram,
      loading: siteLoading,
      siteLoading,
      isSiteReady,
      refresh,
    }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => useContext(SiteContext);
